import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import TeacherNav from '../../../components/TeacherNav';
import { OptionalAppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { StepRenderer } from '../../../components/v2/StepRenderer';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { assignmentsApi, exportApi } from '../../../services/apiV2';

interface ReviewStep {
  step_id: number;
  title: string;
  order: number;
  stage_tag?: string | null;
  counts_as_evidence: boolean;
  step_kind: string;
  content: Record<string, any>;
  signal_config?: Record<string, any>;
  accepts_response?: boolean;
  has_response?: boolean;
  latest_response?: {
    id: number;
    response: Record<string, any>;
    submitted_at: string;
    is_correct?: boolean | null;
    teacher_review?: {
      status: 'reviewed' | 'needs_revision';
      notes: string;
      criteria_notes?: {
        evidence_quality_rating?: WrittenQualityRating;
        [key: string]: unknown;
      };
      reviewed_by: string;
      reviewed_at: string;
    } | null;
    ai_analysis?: {
      id: number;
      result: {
        evidence_summary: string;
        strengths: string[];
        missing_or_unclear: string[];
        follow_up_question: string;
        confidence: 'low' | 'medium' | 'high';
        evidence_excerpts: string[];
        advisory_only: boolean;
      };
      requested_by: string;
      created_at: string;
      privacy: {
        identity_sent: boolean;
        redaction_count: number;
        data_categories: string[];
      };
    } | null;
  } | null;
  answer_key_lines?: string[];
}

interface ReviewResponse {
  id: number;
  lesson_plan: number;
  lesson_plan_title: string;
  section: number;
  section_name: string;
  delivery_mode: 'replay_lesson' | 'replay_exam' | 'evidence_only';
  purpose?: 'makeup' | 'assessment' | 'practice';
  assessment_stage?: string;
  is_active: boolean;
  assigned_at: string;
  due_at: string | null;
  target_scope?: 'all' | 'custom' | 'group' | string;
  target_group_name?: string;
  target_student_count?: number;
  target_summary?: string;
  student: {
    id: number;
    name: string;
  };
  student_status: 'not_started' | 'in_progress' | 'completed' | 'excused' | string;
  makeup_review?: { status: 'pending' | 'needs_revision' | 'approved'; note: string } | null;
  started_at: string | null;
  completed_at: string | null;
  steps: ReviewStep[];
  progress?: {
    total_steps: number;
    answered_count: number;
    scorable_count: number;
    correct_count: number;
    incorrect_count: number;
    score_percent: number | null;
  };
  review?: {
    available: boolean;
    correct_count: number;
    incorrect_count: number;
    scorable_count: number;
    score_percent: number | null;
    missed_steps: Array<{ step_id: number }>;
  };
  observations?: Array<{
    id: number;
    lesson_step_id?: number | null;
    kind: 'hands_on' | 'verbal';
    outcome: string;
    skill_label: string;
    notes: string;
    observed_by: string;
  }>;
  verification?: {
    teacher_feedback: string;
    evidence_summary: string;
    verified_by: string;
    verified_at: string;
  } | null;
  external_submission?: {
    confirmed: boolean;
    confirmed_at: string | null;
  };
  assessment_workflow?: {
    triage: {
      statuses: string[];
      objective_correct_count: number;
      objective_answered_count: number;
      objective_item_count: number;
      objective_score_percent: number | null;
      written_response_count: number;
      written_reviewed_count: number;
      written_pending_count: number;
      written_needs_revision_count: number;
      ai_analyzed_count: number;
      follow_up_recommended_count: number;
      required_artifact_count: number;
      missing_artifact_count: number;
      required_observation_count: number;
      pending_observation_count: number;
      teacher_verified: boolean;
      headrush_upload_confirmed: boolean;
    };
    blockers: string[];
    pdf_available: boolean;
    teacher_review_complete: boolean;
    teacher_verification_ready: boolean;
    teacher_verified: boolean;
    recommended_for_headrush_upload: boolean;
    headrush_upload_confirmed: boolean;
    navigation: {
      position: number;
      total: number;
      previous: ReviewNavigationItem | null;
      next: ReviewNavigationItem | null;
      next_flagged: ReviewNavigationItem | null;
    };
  } | null;
  assessment_ai_review?: {
    enabled: boolean;
    identity_sent: boolean;
    advisory_only: boolean;
  };
  retake_workflow?: {
    policy: 'automatic' | 'teacher_approval' | 'disabled';
    remediation_instructions: string;
    remediation_required: boolean;
    can_request: boolean;
    can_start: boolean;
    current_attempt_context?: {
      teacher_note: string;
      opened_by: string;
      opened_at: string | null;
    } | null;
    request: {
      id: number;
      status: 'requested' | 'approved' | 'denied' | 'used';
      remediation_response: string;
      required_remediation: string[];
      teacher_note: string;
      requested_at: string;
      decided_by: string;
      decided_at: string | null;
    } | null;
  };
  retake_remediation_suggestions?: Array<{
    id: string;
    category: 'missed_objective' | 'written_revision' | 'saved_advisory_analysis' | string;
    title: string;
    requirement: string;
    source_step_id: number;
    advisory_only: boolean;
  }>;
}

interface ReviewNavigationItem {
  student_id: number;
  student_name: string;
  student_status: string;
  triage_statuses: string[];
}

type WrittenQualityRating = 'exceeds' | 'meets' | 'approaching' | 'beginning';

const writtenQualityOptions: Array<{ value: WrittenQualityRating; label: string; description: string }> = [
  { value: 'exceeds', label: 'Exceeds', description: 'Thorough, accurate, and well supported.' },
  { value: 'meets', label: 'Meets', description: 'Accurate and sufficient evidence.' },
  { value: 'approaching', label: 'Approaching', description: 'Partial understanding with gaps.' },
  { value: 'beginning', label: 'Beginning', description: 'Limited evidence or major misconceptions.' },
];

const workflowBlockerLabels: Record<string, string> = {
  student_not_complete: 'Student has not completed the assessment.',
  written_reviews_pending: 'One or more written responses still need teacher review.',
  written_revisions_pending: 'One or more written responses are marked needs revision.',
  required_artifacts_missing: 'One or more required artifacts are missing.',
  teacher_observations_pending: 'One or more hands-on or verbal tasks still need verification.',
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return 'Not available';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const humanizeValue = (value: string): string => (
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const statusPillClass = (status?: string): string => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'completed') return 'bg-emerald-100 text-emerald-700';
  if (normalized === 'in_progress') return 'bg-amber-100 text-amber-700';
  if (normalized === 'not_started') return 'bg-slate-100 text-slate-700';
  if (normalized === 'excused') return 'bg-rose-100 text-rose-700';
  return 'bg-gray-100 text-gray-700';
};

const correctnessPillClass = (isCorrect?: boolean | null): string => {
  if (isCorrect === true) return 'bg-emerald-100 text-emerald-700';
  if (isCorrect === false) return 'bg-rose-100 text-rose-700';
  return 'bg-slate-100 text-slate-700';
};

const getOptions = (step: ReviewStep): string[] => {
  const signalConfig = step.signal_config || {};
  const content = step.content || {};
  const raw = signalConfig.options || signalConfig.choices || content.options || content.choices || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item: any) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') {
        if (typeof item.text === 'string') return item.text.trim();
        if (typeof item.label === 'string') return item.label.trim();
        if (typeof item.value === 'string') return item.value.trim();
      }
      return String(item ?? '').trim();
    })
    .filter((item: string) => item.length > 0);
};

const formatOptionByIndex = (step: ReviewStep, value: unknown): string => {
  const options = getOptions(step);
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(parsed) || parsed < 0 || parsed >= options.length) {
    return String(value ?? '').trim();
  }
  const label = parsed < 26 ? String.fromCharCode(65 + parsed) : `#${parsed + 1}`;
  return `${label}. ${options[parsed]}`;
};

const formatStepPrompt = (step: ReviewStep): string => {
  const signalConfig = step.signal_config || {};
  const directPrompt = String(
    signalConfig.prompt
    || signalConfig.question
    || step.content?.prompt_md
    || step.content?.prompt
    || '',
  ).trim();
  if (directPrompt) return directPrompt;

  const blocks = Array.isArray(step.content?.blocks) ? step.content.blocks : [];
  for (const block of blocks) {
    if (!block || typeof block !== 'object') continue;
    const text = String(block.text || block.markdown || block.value || '').trim();
    if (text) return text;
  }
  return '';
};

const hasFullStepContent = (step: ReviewStep): boolean => {
  const content = step.content;
  if (!content || typeof content !== 'object' || Array.isArray(content)) return false;
  const sourceItem = content.source_item ?? content.sourceItem;
  return Boolean(
    (typeof content.prompt_md === 'string' && content.prompt_md.trim())
    || (typeof content.prompt === 'string' && content.prompt.trim())
    || (typeof content.text === 'string' && content.text.trim())
    || (Array.isArray(content.blocks) && content.blocks.length > 0)
    || (sourceItem && typeof sourceItem === 'object' && Object.keys(sourceItem).length > 0)
  );
};

const buildResponseLines = (step: ReviewStep, response: Record<string, any>): string[] => {
  if (!response || typeof response !== 'object' || Array.isArray(response)) return [];

  const lines: string[] = [];
  const seen = new Set<string>();
  const pushLine = (value: string) => {
    const normalized = String(value || '').trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    lines.push(normalized);
  };

  const reasoningText = String(response.reasoning_text || response.reasoning || '').trim();
  if (reasoningText) pushLine(reasoningText);

  if (response.value !== undefined && response.value !== null && response.value !== '') {
    const signalType = String(step.signal_config?.type || step.signal_config?.signal_type || '').trim().toLowerCase();
    if (signalType === 'mcq_single') {
      pushLine(formatOptionByIndex(step, response.value));
    } else {
      pushLine(String(response.value).trim());
    }
  }

  if (Array.isArray(response.selected_indices) && response.selected_indices.length > 0) {
    const rendered = response.selected_indices
      .map((item) => formatOptionByIndex(step, item))
      .filter((item) => item.length > 0);
    if (rendered.length > 0) pushLine(`Selected: ${rendered.join(', ')}`);
  }

  if (Array.isArray(response.ordering) && response.ordering.length > 0) {
    pushLine(`Order: ${response.ordering.map((item) => String(item ?? '').trim()).filter(Boolean).join(' -> ')}`);
  }

  if (response.pairs && typeof response.pairs === 'object' && !Array.isArray(response.pairs)) {
    Object.entries(response.pairs).forEach(([left, right]) => {
      const leftText = String(left || '').trim();
      const rightText = String(right || '').trim();
      if (leftText || rightText) pushLine(`${leftText} -> ${rightText}`);
    });
  }

  Object.entries(response).forEach(([key, value]) => {
    if (['reasoning_text', 'reasoning', 'value', 'selected_indices', 'ordering', 'pairs'].includes(key)) return;
    if (value === null || value === undefined || value === '') return;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      pushLine(`${humanizeValue(key)}: ${String(value)}`);
    }
  });

  if (lines.length === 0 && Object.keys(response).length > 0) {
    pushLine(JSON.stringify(response, null, 2));
  }

  return lines;
};

export const V2TeacherAssignmentReview: React.FC = () => {
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_queue');
  const location = useLocation();
  const { assignmentId, studentId } = useParams<{ assignmentId: string; studentId: string }>();
  const [detail, setDetail] = useState<ReviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [expandedStepIds, setExpandedStepIds] = useState<Set<number>>(new Set());
  const [reviewNotes, setReviewNotes] = useState<Record<number, string>>({});
  const [writtenQualityRatings, setWrittenQualityRatings] = useState<Record<number, WrittenQualityRating | ''>>({});
  const [quickRevisionStepIds, setQuickRevisionStepIds] = useState<Set<number>>(new Set());
  const [quickDefaultRating, setQuickDefaultRating] = useState<WrittenQualityRating | ''>('');
  const [quickReviewAttested, setQuickReviewAttested] = useState(false);
  const [quickReviewMessage, setQuickReviewMessage] = useState('');
  const [detailedReviewOpen, setDetailedReviewOpen] = useState(false);
  const [teacherFeedback, setTeacherFeedback] = useState('');
  const [evidenceSummary, setEvidenceSummary] = useState('');
  const [saving, setSaving] = useState(false);
  const [aiAnalyzingStepIds, setAiAnalyzingStepIds] = useState<Set<number>>(new Set());
  const [aiErrors, setAiErrors] = useState<Record<number, string>>({});
  const [aiBatchProgress, setAiBatchProgress] = useState<{ current: number; total: number } | null>(null);
  const [aiBatchError, setAiBatchError] = useState('');
  const [aiDraftingVerification, setAiDraftingVerification] = useState(false);
  const [aiVerificationDraftError, setAiVerificationDraftError] = useState('');
  const [aiVerificationDraftPrivacy, setAiVerificationDraftPrivacy] = useState<{ redaction_count: number } | null>(null);
  const [retakeTeacherNote, setRetakeTeacherNote] = useState('');
  const [retakeReviewOpen, setRetakeReviewOpen] = useState(false);
  const [selectedRemediationSuggestionIds, setSelectedRemediationSuggestionIds] = useState<Set<string>>(new Set());
  const [customRemediationRequirement, setCustomRemediationRequirement] = useState('');

  useEffect(() => {
    const loadDetail = async () => {
      if (!assignmentId || !studentId) {
        setError('Assignment or student is missing.');
        setLoading(false);
        return;
      }

      setDetail(null);
      setDetailedReviewOpen(false);
      setRetakeReviewOpen(false);
      setExpandedStepIds(new Set());
      window.scrollTo({ top: 0, behavior: 'auto' });
      setLoading(true);
      setError('');
      try {
        const response = await assignmentsApi.teacherDetail(Number(assignmentId), Number(studentId));
        setDetail(response as ReviewResponse);
        setTeacherFeedback(String(response?.verification?.teacher_feedback || ''));
        setEvidenceSummary(String(response?.verification?.evidence_summary || ''));
        setSelectedRemediationSuggestionIds(new Set());
        setCustomRemediationRequirement('');
        setRetakeReviewOpen(false);
        const nextNotes: Record<number, string> = {};
        const nextRatings: Record<number, WrittenQualityRating | ''> = {};
        const nextRevisionStepIds = new Set<number>();
        (response?.steps || []).forEach((step: ReviewStep) => {
          if (step.latest_response?.teacher_review?.notes) nextNotes[step.step_id] = step.latest_response.teacher_review.notes;
          const rating = step.latest_response?.teacher_review?.criteria_notes?.evidence_quality_rating;
          if (rating) nextRatings[step.step_id] = rating;
          if (step.latest_response?.teacher_review?.status === 'needs_revision') nextRevisionStepIds.add(step.step_id);
        });
        setReviewNotes(nextNotes);
        setWrittenQualityRatings(nextRatings);
        setQuickRevisionStepIds(nextRevisionStepIds);
        setQuickDefaultRating('');
        setQuickReviewAttested(false);
        setQuickReviewMessage('');
        setDetailedReviewOpen(false);
        setExpandedStepIds(new Set());
      } catch (err: any) {
        setDetail(null);
        setError(err?.response?.data?.error || 'Could not load make-up results.');
      } finally {
        setLoading(false);
      }
    };

    void loadDetail();
  }, [assignmentId, studentId]);

  const reload = async () => {
    if (!assignmentId || !studentId) return;
    const response = await assignmentsApi.teacherDetail(Number(assignmentId), Number(studentId));
    setDetail(response as ReviewResponse);
  };

  const refreshReview = async () => {
    if (!assignmentId || !studentId) return;
    setRefreshing(true);
    setError('');
    try {
      const response = await assignmentsApi.teacherDetail(Number(assignmentId), Number(studentId));
      setDetail(response as ReviewResponse);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not refresh the assessment review.');
    } finally {
      setRefreshing(false);
    }
  };

  const saveResponseReview = async (step: ReviewStep, reviewStatus: 'reviewed' | 'needs_revision') => {
    if (!step.latest_response?.id) return;
    const qualityRating = writtenQualityRatings[step.step_id];
    if (!qualityRating) {
      setError('Choose an evidence-quality rating before saving this written response review.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await assignmentsApi.reviewResponse(step.latest_response.id, {
        status: reviewStatus,
        notes: reviewNotes[step.step_id] || '',
        criteria_notes: { evidence_quality_rating: qualityRating },
      });
      await reload();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not save the response review.');
    } finally {
      setSaving(false);
    }
  };

  const decideMakeup = async (decision: 'approved' | 'needs_revision') => {
    if (!detail) return;
    const note = decision === 'needs_revision'
      ? window.prompt(`What should ${detail.student.name} revise? A new attempt will open for them.`)
      : '';
    if (decision === 'needs_revision' && !note?.trim()) return;
    setSaving(true);
    setError('');
    try {
      await assignmentsApi.reviewMakeup(detail.id, detail.student.id, { decision, note: note?.trim() });
      await reload();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not save the make-up decision.');
    } finally {
      setSaving(false);
    }
  };

  const analyzeWrittenResponse = async (step: ReviewStep) => {
    if (!step.latest_response?.id) return;
    setAiAnalyzingStepIds((current) => new Set(current).add(step.step_id));
    setAiErrors((current) => ({ ...current, [step.step_id]: '' }));
    try {
      await assignmentsApi.analyzeWrittenResponse(step.latest_response.id);
      await reload();
    } catch (err: any) {
      setAiErrors((current) => ({
        ...current,
        [step.step_id]: err?.response?.data?.error || 'Could not analyze this written response.',
      }));
    } finally {
      setAiAnalyzingStepIds((current) => {
        const next = new Set(current);
        next.delete(step.step_id);
        return next;
      });
    }
  };

  const writtenStepsNeedingAnalysis = (detail?.steps || []).filter((step) => (
    step.latest_response
    && step.latest_response.is_correct == null
    && !step.latest_response.ai_analysis
    && !step.latest_response.teacher_review
  ));
  const quickReviewSteps = (detail?.steps || []).filter((step) => (
    step.accepts_response
    && step.latest_response
    && step.latest_response.is_correct == null
  ));
  const quickReviewUnratedCount = quickReviewSteps.filter((step) => !writtenQualityRatings[step.step_id]).length;

  const applyQuickDefaultRating = () => {
    if (!quickDefaultRating) return;
    setWrittenQualityRatings((current) => {
      const next = { ...current };
      quickReviewSteps.forEach((step) => {
        if (!next[step.step_id]) next[step.step_id] = quickDefaultRating;
      });
      return next;
    });
    setQuickReviewMessage('');
  };

  const toggleQuickRevision = (stepId: number) => {
    setQuickRevisionStepIds((current) => {
      const next = new Set(current);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
    setQuickReviewAttested(false);
    setQuickReviewMessage('');
  };

  const saveQuickReviews = async () => {
    if (!detail || quickReviewSteps.length === 0) return;
    if (quickReviewUnratedCount > 0) {
      setError(`Rate all ${quickReviewSteps.length} written responses before saving.`);
      return;
    }
    if (!quickReviewAttested) {
      setError('Confirm that you reviewed the written responses before saving.');
      return;
    }
    setSaving(true);
    setError('');
    setQuickReviewMessage('');
    try {
      await assignmentsApi.batchReviewResponses(detail.id, detail.student.id, {
        teacher_attestation: true,
        reviews: quickReviewSteps.map((step) => ({
          response_id: Number(step.latest_response?.id),
          status: quickRevisionStepIds.has(step.step_id) ? 'needs_revision' : 'reviewed',
          evidence_quality_rating: writtenQualityRatings[step.step_id] as WrittenQualityRating,
          notes: reviewNotes[step.step_id] || '',
        })),
      });
      await reload();
      setQuickReviewAttested(false);
      setQuickReviewMessage(`${quickReviewSteps.length} written responses saved.`);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not save the quick review.');
    } finally {
      setSaving(false);
    }
  };

  const analyzeAllWrittenResponses = async () => {
    const targets = writtenStepsNeedingAnalysis.filter((step) => step.latest_response?.id);
    if (targets.length === 0) return;
    setAiBatchError('');
    setAiBatchProgress({ current: 0, total: targets.length });
    setAiAnalyzingStepIds((current) => new Set([
      ...Array.from(current),
      ...targets.map((step) => step.step_id),
    ]));
    const failures: string[] = [];
    for (let index = 0; index < targets.length; index += 1) {
      const step = targets[index];
      try {
        await assignmentsApi.analyzeWrittenResponse(step.latest_response!.id);
      } catch (err: any) {
        failures.push(`${step.title || `Question ${step.order}`}: ${err?.response?.data?.error || 'analysis failed'}`);
      }
      setAiBatchProgress({ current: index + 1, total: targets.length });
    }
    try {
      await reload();
    } catch (err: any) {
      failures.push(err?.response?.data?.error || 'Could not refresh the completed analyses.');
    }
    setAiAnalyzingStepIds((current) => {
      const next = new Set(current);
      targets.forEach((step) => next.delete(step.step_id));
      return next;
    });
    setAiBatchProgress(null);
    if (failures.length > 0) {
      setAiBatchError(`${targets.length - failures.length} of ${targets.length} responses analyzed. ${failures.join(' ')}`);
    }
  };

  const addObservation = async (kind: 'hands_on' | 'verbal', step?: ReviewStep) => {
    if (!assignmentId || !studentId) return;
    const observationConfig = step?.content?.observation_config && typeof step.content.observation_config === 'object'
      ? step.content.observation_config
      : {};
    const suggestedLabel = String(observationConfig.skill_label || step?.title || '').trim();
    const label = window.prompt(
      kind === 'hands_on' ? 'Skill observed:' : 'Explanation or demonstration verified:',
      suggestedLabel,
    );
    if (!label?.trim()) return;
    const notes = window.prompt('Teacher note (optional):') || '';
    setSaving(true);
    try {
      await assignmentsApi.addObservation(Number(assignmentId), Number(studentId), {
        kind,
        outcome: 'verified',
        skill_label: label.trim(),
        notes,
        ...(step ? { lesson_step_id: step.step_id } : {}),
      });
      await reload();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not save the observation.');
    } finally {
      setSaving(false);
    }
  };

  const saveVerification = async () => {
    if (!assignmentId || !studentId) return;
    setSaving(true);
    try {
      await assignmentsApi.verify(Number(assignmentId), Number(studentId), {
        teacher_feedback: teacherFeedback,
        evidence_summary: evidenceSummary,
      });
      await reload();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not save teacher verification.');
    } finally {
      setSaving(false);
    }
  };

  const draftVerification = async () => {
    if (!assignmentId || !studentId) return;
    if ((evidenceSummary.trim() || teacherFeedback.trim()) && !window.confirm('Replace the current summary and feedback with a new AI draft?')) return;
    setAiDraftingVerification(true);
    setAiVerificationDraftError('');
    setAiVerificationDraftPrivacy(null);
    try {
      const response = await assignmentsApi.draftVerification(Number(assignmentId), Number(studentId));
      setEvidenceSummary(String(response?.draft?.evidence_summary || ''));
      setTeacherFeedback(String(response?.draft?.teacher_feedback || ''));
      setAiVerificationDraftPrivacy({ redaction_count: Number(response?.privacy?.redaction_count || 0) });
    } catch (err: any) {
      setAiVerificationDraftError(err?.response?.data?.error || 'Could not generate the advisory verification draft.');
    } finally {
      setAiDraftingVerification(false);
    }
  };

  const decideRetake = async (decision: 'approved' | 'denied') => {
    if (!assignmentId || !studentId) return;
    setSaving(true);
    setError('');
    try {
      const selectedRequirements = (detail?.retake_remediation_suggestions || [])
        .filter((suggestion) => selectedRemediationSuggestionIds.has(suggestion.id))
        .map((suggestion) => suggestion.requirement);
      if (customRemediationRequirement.trim()) selectedRequirements.push(customRemediationRequirement.trim());
      await assignmentsApi.decideRetake(Number(assignmentId), Number(studentId), {
        decision,
        teacher_note: retakeTeacherNote.trim(),
        required_remediation: decision === 'denied' ? selectedRequirements : [],
      });
      setRetakeTeacherNote('');
      setSelectedRemediationSuggestionIds(new Set());
      setCustomRemediationRequirement('');
      await reload();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not update the retake request.');
    } finally {
      setSaving(false);
    }
  };

  const requireNewAttempt = async () => {
    if (!detail || detail.purpose !== 'assessment' || detail.student_status !== 'completed') return;
    const reason = window.prompt(
      `Why must ${detail.student.name} complete a new attempt? Their previous attempt will remain in the record.`,
    );
    if (!reason?.trim()) return;
    setSaving(true);
    setError('');
    try {
      await assignmentsApi.requireRetake(detail.id, detail.student.id, { reason: reason.trim() });
      await reload();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not require a new assessment attempt.');
    } finally {
      setSaving(false);
    }
  };

  const toggleRemediationSuggestion = (suggestionId: string) => {
    setSelectedRemediationSuggestionIds((current) => {
      const next = new Set(current);
      if (next.has(suggestionId)) next.delete(suggestionId);
      else next.add(suggestionId);
      return next;
    });
  };

  const downloadEvidence = async () => {
    if (!assignmentId || !studentId || !detail) return;
    setSaving(true);
    try {
      const generated = await exportApi.generateAssessmentEvidence(Number(assignmentId), Number(studentId));
      const response = await exportApi.download(Number(generated.export_id));
      const blobUrl = window.URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = `${detail.student.name.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}_assessment_evidence.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.response?.data?.error || 'Could not generate the evidence PDF.');
    } finally {
      setSaving(false);
    }
  };

  const navigationState = location.state as { queueReturn?: unknown; queueScrollY?: unknown } | null;
  const savedQueueReturn = typeof navigationState?.queueReturn === 'string'
    && navigationState.queueReturn.startsWith('/v2/teacher/queue')
    ? navigationState.queueReturn
    : '';
  const backToQueue = savedQueueReturn || (detail?.purpose === 'assessment' ? '/v2/teacher/queue?purpose=assessment' : `/v2/teacher/queue?purpose=${detail?.purpose === 'practice' ? 'practice' : 'makeup'}`);
  const savedQueueScrollY = Number(navigationState?.queueScrollY);
  const backNavigationState = Number.isFinite(savedQueueScrollY) && savedQueueScrollY > 0
    ? { restoreScrollY: savedQueueScrollY }
    : undefined;

  return (
    <OptionalAppShell
      enabled={redesignEnabled}
      role="teacher"
      navItems={teacherV2NavItems('queue')}
      title={detail?.purpose === 'assessment' ? 'Assessment Review' : detail?.purpose === 'practice' ? 'Unit Practice Review' : 'Make-up Results'}
    >
      {!redesignEnabled && <TeacherNav activeTab="queue" />}
      <div className={redesignEnabled ? 'mx-auto max-w-5xl space-y-4' : 'mx-auto max-w-5xl p-6'}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{detail?.purpose === 'assessment' ? 'Assessment Review' : detail?.purpose === 'practice' ? 'Unit Practice Review' : 'Make-up Results'}</h1>
            <p className="mt-1 text-sm text-gray-600">
              {detail?.purpose === 'assessment'
                ? 'Review written evidence, record demonstrations, and prepare the student’s Headrush-ready document.'
                : 'Review one student’s saved responses for a replay assignment.'}
            </p>
          </div>
          <Link
            to={backToQueue}
            state={backNavigationState}
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700"
          >
            {detail?.purpose === 'assessment' ? 'Back to Assessment Triage' : 'Back to Queue'}
          </Link>
        </div>

        {loading ? (
          <div className="rounded border border-gray-200 bg-white px-4 py-4 text-sm text-gray-500">
            Loading results...
          </div>
        ) : error ? (
          <div className="rounded border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">
            {error}
          </div>
        ) : !detail ? (
          <div className="rounded border border-gray-200 bg-white px-4 py-4 text-sm text-gray-500">
            No assignment results were found.
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Student</p>
                  <h2 className="text-xl font-semibold text-gray-900">{detail.student.name}</h2>
                  <p className="mt-1 text-sm text-gray-600">
                    {detail.lesson_plan_title} | {detail.section_name}
                  </p>
                </div>
                <span className={`rounded-full px-3 py-1 text-sm font-medium ${statusPillClass(detail.student_status)}`}>
                  {humanizeValue(detail.student_status)}
                </span>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Delivery mode</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">{humanizeValue(detail.delivery_mode)}</p>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Assigned</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">{formatDateTime(detail.assigned_at)}</p>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Completed</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">{formatDateTime(detail.completed_at)}</p>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Target</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">{detail.target_summary || 'All students'}</p>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Answered</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">
                    {detail.progress?.answered_count ?? 0} / {detail.progress?.total_steps ?? 0}
                  </p>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Score</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">
                    {detail.review?.score_percent ?? detail.progress?.score_percent ?? 'Not available'}
                    {detail.review?.score_percent !== null && detail.review?.score_percent !== undefined ? '%' : ''}
                  </p>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Missed steps</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">{detail.review?.missed_steps?.length ?? 0}</p>
                </div>
              </div>
            </div>

            {['makeup', 'practice'].includes(detail.purpose || '') && (
              <section className="rounded-lg border border-sky-200 bg-sky-50 p-4">
                <h2 className="text-lg font-semibold text-sky-950">Make-up decision</h2>
                <p className="mt-1 text-sm text-sky-900">
                  {detail.makeup_review?.status === 'approved' ? 'Reviewed and complete. This remains in the student’s record.'
                    : detail.makeup_review?.status === 'needs_revision' ? `Revision requested: ${detail.makeup_review.note}`
                      : detail.student_status === 'completed' ? 'Submitted and awaiting your review.' : 'Waiting for the student to finish.'}
                </p>
                {detail.student_status === 'completed' && detail.makeup_review?.status !== 'approved' && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" disabled={saving} onClick={() => { void decideMakeup('approved'); }} className="rounded bg-emerald-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">Mark make-up reviewed</button>
                    {detail.delivery_mode !== 'evidence_only' && <button type="button" disabled={saving} onClick={() => { void decideMakeup('needs_revision'); }} className="rounded border border-amber-300 bg-white px-3 py-2 text-sm font-semibold text-amber-800 disabled:opacity-60">Send make-up back for revision</button>}
                  </div>
                )}
                <p className="mt-2 text-xs text-sky-800">Reviewing an individual written response below does not finish the make-up decision.</p>
              </section>
            )}

            {detail.purpose === 'assessment' && detail.assessment_workflow && (
              <section className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-indigo-950">Assessment workflow</h2>
                    <p className="mt-1 text-sm text-indigo-900">
                      Student {detail.assessment_workflow.navigation.position} of {detail.assessment_workflow.navigation.total} for this assessment.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {detail.assessment_workflow.navigation.previous && (
                      <Link
                        to={`/v2/teacher/assignments/${detail.id}/students/${detail.assessment_workflow.navigation.previous.student_id}`}
                        state={location.state}
                        className="rounded border border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-800"
                      >
                        Previous student
                      </Link>
                    )}
                    {detail.assessment_workflow.navigation.next && (
                      <Link
                        to={`/v2/teacher/assignments/${detail.id}/students/${detail.assessment_workflow.navigation.next.student_id}`}
                        state={location.state}
                        className="rounded border border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-800"
                      >
                        Next student
                      </Link>
                    )}
                    {detail.assessment_workflow.navigation.next_flagged && (
                      <Link
                        to={`/v2/teacher/assignments/${detail.id}/students/${detail.assessment_workflow.navigation.next_flagged.student_id}`}
                        state={location.state}
                        className="rounded bg-indigo-700 px-3 py-1.5 text-xs font-semibold text-white"
                      >
                        Next flagged: {detail.assessment_workflow.navigation.next_flagged.student_name}
                      </Link>
                    )}
                  </div>
                </div>

                {detail.student_status === 'completed' && ['replay_exam', 'replay_lesson'].includes(detail.delivery_mode) && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded border border-amber-300 bg-amber-50 p-3">
                    <div>
                      <p className="text-sm font-semibold text-amber-950">Whole-assessment reassessment</p>
                      <p className="mt-1 text-xs text-amber-900">Open a fresh attempt without reviewing each response. The completed attempt remains in the student record.</p>
                    </div>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => { void requireNewAttempt(); }}
                      className="rounded border border-amber-400 bg-white px-3 py-2 text-sm font-semibold text-amber-900 disabled:opacity-50"
                    >
                      Require new attempt
                    </button>
                  </div>
                )}

                <div className="mt-4 grid gap-2 md:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded border border-indigo-100 bg-white p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Evidence PDF</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {detail.assessment_workflow.pdf_available ? 'Available to download' : 'Waiting for student completion'}
                    </p>
                  </div>
                  <div className="rounded border border-indigo-100 bg-white p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Evidence review</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {detail.assessment_workflow.teacher_review_complete ? 'Review complete' : 'Teacher action needed'}
                    </p>
                  </div>
                  <div className="rounded border border-indigo-100 bg-white p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Teacher verification</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {detail.assessment_workflow.teacher_verified
                        ? 'Verification saved'
                        : detail.assessment_workflow.teacher_verification_ready ? 'Ready to verify' : 'Not ready'}
                    </p>
                  </div>
                  <div className="rounded border border-indigo-100 bg-white p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Headrush handoff</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {detail.assessment_workflow.headrush_upload_confirmed
                        ? 'Student confirmed upload'
                        : detail.assessment_workflow.recommended_for_headrush_upload ? 'Recommended for student upload' : 'Not yet verified'}
                    </p>
                  </div>
                </div>

                {detail.assessment_ai_review?.enabled && writtenStepsNeedingAnalysis.length > 0 && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded border border-violet-200 bg-violet-50 p-3">
                    <div>
                      <p className="text-sm font-semibold text-violet-950">Written-response AI review</p>
                      <p className="mt-1 text-xs text-violet-900">
                        {writtenStepsNeedingAnalysis.length} saved written response{writtenStepsNeedingAnalysis.length === 1 ? '' : 's'} still need advisory analysis. Student identity and roster data are not sent.
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={Boolean(aiBatchProgress)}
                      onClick={() => { void analyzeAllWrittenResponses(); }}
                      className="rounded bg-violet-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                    >
                      {aiBatchProgress
                        ? `Analyzing ${aiBatchProgress.current} of ${aiBatchProgress.total}…`
                        : `Analyze all ${writtenStepsNeedingAnalysis.length}`}
                    </button>
                  </div>
                )}
                {aiBatchError && (
                  <p className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{aiBatchError}</p>
                )}

                {detail.retake_workflow?.request?.status === 'requested' && (
                  <div className="mt-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold">Retake approval requested</p>
                        <p className="mt-1 text-xs text-amber-800">
                          {(detail.retake_remediation_suggestions || []).length > 0
                            ? `${(detail.retake_remediation_suggestions || []).length} evidence-informed preparation option${(detail.retake_remediation_suggestions || []).length === 1 ? '' : 's'} available.`
                            : 'Review the request and choose whether to approve another attempt.'}
                        </p>
                      </div>
                      <button
                        type="button"
                        aria-expanded={retakeReviewOpen}
                        onClick={() => setRetakeReviewOpen((current) => !current)}
                        className="rounded border border-amber-400 bg-white px-3 py-2 text-xs font-semibold text-amber-950"
                      >
                        {retakeReviewOpen ? 'Hide retake request' : 'Review retake request'}
                      </button>
                    </div>
                    {retakeReviewOpen && <div className="mt-3 border-t border-amber-200 pt-3">
                      {detail.retake_workflow.remediation_instructions && (
                        <p><span className="font-medium">Required preparation:</span> {detail.retake_workflow.remediation_instructions}</p>
                      )}
                      {detail.retake_workflow.request.remediation_response && (
                        <p className="mt-2 whitespace-pre-wrap rounded border border-amber-200 bg-white p-2">
                          <span className="font-medium">Student response:</span> {detail.retake_workflow.request.remediation_response}
                        </p>
                      )}
                      {(detail.retake_workflow.request.required_remediation || []).length > 0 && (
                        <div className="mt-2 rounded border border-amber-200 bg-white p-2">
                          <p className="font-medium">Previously required preparation:</p>
                          <ul className="mt-1 list-disc space-y-1 pl-5">
                            {(detail.retake_workflow.request.required_remediation || []).map((requirement, index) => (
                              <li key={`required-remediation-${index}`}>{requirement}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="mt-3 rounded border border-amber-200 bg-white p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="font-semibold">Advisory remediation options</p>
                          <p className="text-xs text-amber-800">Derived locally from missed objectives, teacher-reviewed writing, and saved advisory analysis. Select only what this student must complete.</p>
                        </div>
                        {(detail.retake_remediation_suggestions || []).length > 0 && (
                          <button
                            type="button"
                            className="rounded border border-amber-300 px-2 py-1 text-xs font-semibold"
                            onClick={() => setSelectedRemediationSuggestionIds(new Set((detail.retake_remediation_suggestions || []).map((suggestion) => suggestion.id)))}
                          >
                            Select all
                          </button>
                        )}
                      </div>
                      {(detail.retake_remediation_suggestions || []).length > 0 ? (
                        <div className="mt-3 space-y-2">
                          {(detail.retake_remediation_suggestions || []).map((suggestion) => (
                            <label key={suggestion.id} className="flex items-start gap-2 rounded border border-gray-200 bg-gray-50 p-2">
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={selectedRemediationSuggestionIds.has(suggestion.id)}
                                onChange={() => toggleRemediationSuggestion(suggestion.id)}
                              />
                              <span>
                                <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">{humanizeValue(suggestion.category)}</span>
                                <span className="block text-sm text-gray-900">{suggestion.requirement}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-2 text-xs text-gray-600">No evidence-specific options were found. Add a custom requirement below if more preparation is needed.</p>
                      )}
                      <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-gray-600" htmlFor="custom-remediation-requirement">Custom requirement (optional)</label>
                      <textarea
                        id="custom-remediation-requirement"
                        className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                        rows={2}
                        value={customRemediationRequirement}
                        onChange={(event) => setCustomRemediationRequirement(event.target.value)}
                        placeholder="Add preparation that is not covered by the suggestions."
                      />
                      </div>
                      <label className="mt-3 block font-medium" htmlFor="retake-teacher-note">Teacher note (optional)</label>
                      <textarea
                        id="retake-teacher-note"
                        className="mt-1 w-full rounded border border-amber-300 bg-white px-3 py-2"
                        rows={2}
                        value={retakeTeacherNote}
                        onChange={(event) => setRetakeTeacherNote(event.target.value)}
                      />
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button type="button" disabled={saving} onClick={() => { void decideRetake('approved'); }} className="rounded bg-emerald-700 px-3 py-1.5 font-semibold text-white disabled:opacity-50">Approve retake</button>
                        <button
                          type="button"
                          disabled={saving || (selectedRemediationSuggestionIds.size === 0 && !customRemediationRequirement.trim() && !retakeTeacherNote.trim())}
                          onClick={() => { void decideRetake('denied'); }}
                          className="rounded border border-amber-400 bg-white px-3 py-1.5 font-semibold text-amber-900 disabled:opacity-50"
                        >
                          Require selected preparation
                        </button>
                      </div>
                    </div>}
                  </div>
                )}

                {detail.assessment_workflow.blockers.length > 0 ? (
                  <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    <p className="font-semibold">Before final teacher verification:</p>
                    <ul className="mt-1 list-disc space-y-1 pl-5">
                      {detail.assessment_workflow.blockers.map((blocker) => (
                        <li key={blocker}>{workflowBlockerLabels[blocker] || humanizeValue(blocker)}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="mt-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                    All collected evidence has been reviewed. Save teacher verification when ready.
                  </p>
                )}
                <p className="mt-3 text-xs text-indigo-800">
                  PDF availability and Headrush readiness are separate. Headrush remains the official place for Level Up and NP/P/M decisions.
                </p>
              </section>
            )}

            {detail.purpose === 'assessment' && detail.student_status === 'completed' && quickReviewSteps.length > 0 && (
              <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" aria-labelledby="quick-review-heading">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 id="quick-review-heading" className="text-lg font-semibold text-emerald-950">Quick written-response review</h2>
                    <p className="mt-1 text-sm text-emerald-900">Rate all written evidence here, flag only the exceptions that need revision, then save once. Instruction-only and observed evidence are excluded.</p>
                  </div>
                  <span className="rounded-full border border-emerald-300 bg-white px-3 py-1 text-xs font-semibold text-emerald-900">
                    {quickReviewSteps.length - quickReviewUnratedCount}/{quickReviewSteps.length} rated
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap items-end gap-2 rounded border border-emerald-200 bg-white p-3">
                  <label className="text-sm font-medium text-gray-900" htmlFor="quick-default-rating">
                    Set all unrated responses to
                    <select
                      id="quick-default-rating"
                      className="ml-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm"
                      value={quickDefaultRating}
                      onChange={(event) => setQuickDefaultRating(event.target.value as WrittenQualityRating | '')}
                    >
                      <option value="">Choose quality</option>
                      {writtenQualityOptions.map((option) => <option key={`quick-default-${option.value}`} value={option.value}>{option.label}</option>)}
                    </select>
                  </label>
                  <button
                    type="button"
                    disabled={!quickDefaultRating || quickReviewUnratedCount === 0}
                    onClick={applyQuickDefaultRating}
                    className="rounded border border-emerald-400 px-3 py-2 text-sm font-semibold text-emerald-900 disabled:opacity-50"
                  >
                    Apply to {quickReviewUnratedCount} unrated
                  </button>
                </div>

                <div className="mt-4 space-y-3">
                  {quickReviewSteps.map((step) => {
                    const preview = buildResponseLines(step, step.latest_response?.response || {}).join(' ').slice(0, 320);
                    return (
                      <article key={`quick-review-${step.step_id}`} className="rounded border border-emerald-200 bg-white p-3">
                        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px_auto] lg:items-start">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800">Question {step.order}</p>
                            <p className="mt-1 text-sm font-medium text-gray-900">{formatStepPrompt(step) || step.title}</p>
                            <p className="mt-2 line-clamp-3 whitespace-pre-wrap rounded bg-gray-50 px-3 py-2 text-sm text-gray-700">{preview || 'No readable response preview.'}</p>
                          </div>
                          <label className="text-sm font-medium text-gray-900" htmlFor={`quick-quality-${step.step_id}`}>
                            Evidence quality
                            <select
                              id={`quick-quality-${step.step_id}`}
                              className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
                              value={writtenQualityRatings[step.step_id] || ''}
                              onChange={(event) => {
                                setWrittenQualityRatings((current) => ({ ...current, [step.step_id]: event.target.value as WrittenQualityRating | '' }));
                                setQuickReviewAttested(false);
                                setQuickReviewMessage('');
                              }}
                            >
                              <option value="">Not yet rated</option>
                              {writtenQualityOptions.map((option) => <option key={`quick-${step.step_id}-${option.value}`} value={option.value}>{option.label}</option>)}
                            </select>
                          </label>
                          <label className="flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-950">
                            <input type="checkbox" checked={quickRevisionStepIds.has(step.step_id)} onChange={() => toggleQuickRevision(step.step_id)} />
                            Needs revision
                          </label>
                        </div>
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs font-semibold text-emerald-800">Optional response-specific feedback</summary>
                          <textarea
                            className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                            rows={2}
                            value={reviewNotes[step.step_id] || ''}
                            onChange={(event) => {
                              setReviewNotes((current) => ({ ...current, [step.step_id]: event.target.value }));
                              setQuickReviewAttested(false);
                              setQuickReviewMessage('');
                            }}
                            aria-label={`Feedback for question ${step.order}`}
                          />
                        </details>
                      </article>
                    );
                  })}
                </div>

                <div className="mt-4 rounded border border-emerald-200 bg-white p-3">
                  <label className="flex items-start gap-2 text-sm text-gray-900">
                    <input type="checkbox" className="mt-1" checked={quickReviewAttested} onChange={(event) => setQuickReviewAttested(event.target.checked)} />
                    <span>I reviewed these written responses and confirmed the evidence-quality ratings and revision flags.</span>
                  </label>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      disabled={saving || quickReviewUnratedCount > 0 || !quickReviewAttested}
                      onClick={() => { void saveQuickReviews(); }}
                      className="rounded bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    >
                      {saving ? 'Saving reviews…' : `Save all ${quickReviewSteps.length} reviews`}
                    </button>
                    {quickReviewUnratedCount > 0 && <span className="text-xs text-amber-800">Rate {quickReviewUnratedCount} remaining response{quickReviewUnratedCount === 1 ? '' : 's'}.</span>}
                    {quickReviewMessage && <span role="status" className="text-sm font-semibold text-emerald-800">{quickReviewMessage}</span>}
                  </div>
                </div>
              </section>
            )}

            {detail.purpose === 'assessment' && detail.student_status === 'completed' && quickReviewSteps.length > 0 && (
              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Detailed question review</h2>
                    <p className="mt-1 text-sm text-gray-600">Open full prompts, answer keys, individual AI analysis, and per-response review controls only when you need them.</p>
                  </div>
                  <button
                    type="button"
                    aria-expanded={detailedReviewOpen}
                    onClick={() => setDetailedReviewOpen((current) => !current)}
                    className="rounded border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800"
                  >
                    {detailedReviewOpen ? 'Hide detailed question review' : `Show detailed question review (${detail.steps.length})`}
                  </button>
                </div>
              </section>
            )}

            {(detail.purpose !== 'assessment' || detail.student_status !== 'completed' || quickReviewSteps.length === 0 || detailedReviewOpen) && <div className="space-y-3">
              {detail.steps.map((step) => {
                const responseLines = buildResponseLines(step, step.latest_response?.response || {});
                const promptText = formatStepPrompt(step);
                const canShowFullContent = hasFullStepContent(step);
                const isExpanded = expandedStepIds.has(step.step_id);
                return (
                  <div key={step.step_id} className="rounded-lg border border-gray-200 bg-white p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">
                          Step {step.order}
                          {step.stage_tag ? ` | ${humanizeValue(step.stage_tag)}` : ''}
                          {step.step_kind ? ` | ${humanizeValue(step.step_kind)}` : ''}
                        </p>
                        <h3 className="mt-1 text-lg font-semibold text-gray-900">{step.title || `Step ${step.order}`}</h3>
                        {promptText && (
                          <p className="mt-2 whitespace-pre-wrap text-sm text-gray-600">{promptText}</p>
                        )}
                        {canShowFullContent && (
                          <button
                            type="button"
                            className="mt-3 text-sm font-medium text-indigo-700 hover:text-indigo-800"
                            onClick={() => {
                              setExpandedStepIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(step.step_id)) {
                                  next.delete(step.step_id);
                                } else {
                                  next.add(step.step_id);
                                }
                                return next;
                              });
                            }}
                          >
                            {isExpanded ? 'Hide full step content' : 'Show full step content'}
                          </button>
                        )}
                      </div>
                      {step.accepts_response && (
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${correctnessPillClass(step.latest_response?.is_correct)}`}>
                          {step.latest_response?.is_correct === true
                            ? 'Correct'
                            : step.latest_response?.is_correct === false
                              ? 'Incorrect'
                              : 'Saved'}
                        </span>
                      )}
                    </div>

                    {canShowFullContent && isExpanded && (
                      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs uppercase tracking-wide text-slate-500">Full step content</p>
                        <div className="mt-3">
                          <StepRenderer
                            title={step.title}
                            stageTag={step.stage_tag}
                            stepKind={step.step_kind}
                            content={step.content}
                            hideHeader
                          />
                        </div>
                      </div>
                    )}

                    {!step.accepts_response ? (
                      <p className="mt-3 text-sm text-gray-500">Instruction-only step. No saved response is expected here.</p>
                    ) : !step.latest_response ? (
                      <p className="mt-3 text-sm text-gray-500">No saved response yet.</p>
                    ) : (
                      <div className="mt-3 space-y-3">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">Submitted</p>
                          <p className="mt-1 text-sm text-gray-900">{formatDateTime(step.latest_response.submitted_at)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">Student response</p>
                          {responseLines.length === 0 ? (
                            <p className="mt-1 text-sm text-gray-500">No readable response preview available.</p>
                          ) : (
                            <div className="mt-2 space-y-2">
                              {responseLines.map((line, index) => (
                                <pre
                                  key={`${step.step_id}-response-${index}`}
                                  className="whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800"
                                >
                                  {line}
                                </pre>
                              ))}
                            </div>
                          )}
                        </div>
                        {Array.isArray(step.answer_key_lines) && step.answer_key_lines.length > 0 && (
                          <div>
                            <p className="text-xs uppercase tracking-wide text-gray-500">Answer key</p>
                            <div className="mt-2 space-y-2">
                              {step.answer_key_lines.map((line, index) => (
                                <div
                                  key={`${step.step_id}-answer-${index}`}
                                  className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
                                >
                                  {line}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {detail.purpose === 'assessment' && step.latest_response.is_correct == null && (
                          <div className="space-y-3">
                            <div className="rounded border border-violet-200 bg-violet-50 p-3">
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                  <p className="text-xs font-semibold uppercase tracking-wide text-violet-800">AI evidence check — advisory only</p>
                                  <p className="mt-1 text-xs text-violet-900">Only the question, anonymous response, authored criteria, and relevant standards are sent. Known student identity, email, username, phone, URL, and roster data are not sent. Review the response for any other personal details before requesting analysis.</p>
                                </div>
                                {detail.assessment_ai_review?.enabled && (
                                  <button type="button" disabled={aiAnalyzingStepIds.has(step.step_id)} onClick={() => { void analyzeWrittenResponse(step); }} className="rounded bg-violet-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                                    {aiAnalyzingStepIds.has(step.step_id) ? 'Analyzing…' : step.latest_response.ai_analysis ? 'Analyze again' : 'Analyze written response'}
                                  </button>
                                )}
                              </div>
                              {!detail.assessment_ai_review?.enabled && <p className="mt-2 text-xs font-semibold text-violet-800">Privacy-controlled AI review is currently disabled.</p>}
                              {aiErrors[step.step_id] && <p className="mt-2 rounded border border-rose-200 bg-rose-50 px-2 py-1.5 text-xs text-rose-700">{aiErrors[step.step_id]}</p>}
                              {step.latest_response.ai_analysis && (
                                <div className="mt-3 space-y-3 rounded border border-violet-200 bg-white p-3 text-sm text-gray-800">
                                  <div><p className="text-xs font-semibold uppercase text-gray-500">Evidence summary</p><p className="mt-1">{step.latest_response.ai_analysis.result.evidence_summary}</p></div>
                                  {step.latest_response.ai_analysis.result.evidence_excerpts.length > 0 && <div><p className="text-xs font-semibold uppercase text-gray-500">Exact evidence excerpts</p><ul className="mt-1 list-disc space-y-1 pl-5">{step.latest_response.ai_analysis.result.evidence_excerpts.map((item, index) => <li key={`excerpt-${index}`}>&ldquo;{item}&rdquo;</li>)}</ul></div>}
                                  {step.latest_response.ai_analysis.result.strengths.length > 0 && <div><p className="text-xs font-semibold uppercase text-gray-500">Strengths</p><ul className="mt-1 list-disc space-y-1 pl-5">{step.latest_response.ai_analysis.result.strengths.map((item, index) => <li key={`strength-${index}`}>{item}</li>)}</ul></div>}
                                  {step.latest_response.ai_analysis.result.missing_or_unclear.length > 0 && <div><p className="text-xs font-semibold uppercase text-gray-500">Missing or unclear</p><ul className="mt-1 list-disc space-y-1 pl-5">{step.latest_response.ai_analysis.result.missing_or_unclear.map((item, index) => <li key={`missing-${index}`}>{item}</li>)}</ul></div>}
                                  {step.latest_response.ai_analysis.result.follow_up_question && <div><p className="text-xs font-semibold uppercase text-gray-500">Suggested teacher follow-up</p><p className="mt-1">{step.latest_response.ai_analysis.result.follow_up_question}</p></div>}
                                  <div className="flex flex-wrap items-center justify-between gap-2 border-t border-violet-100 pt-2 text-xs text-gray-500"><span>Confidence: {humanizeValue(step.latest_response.ai_analysis.result.confidence)}</span><span>Identity sent: No{step.latest_response.ai_analysis.privacy.redaction_count ? ` · ${step.latest_response.ai_analysis.privacy.redaction_count} personal-data redaction(s)` : ''}</span></div>
                                  <button type="button" onClick={() => setReviewNotes((current) => ({ ...current, [step.step_id]: step.latest_response?.ai_analysis?.result.evidence_summary || current[step.step_id] || '' }))} className="rounded border border-violet-300 px-3 py-1.5 text-xs font-semibold text-violet-800">Use summary in review notes</button>
                                </div>
                              )}
                            </div>
                            <div className="rounded border border-indigo-200 bg-indigo-50 p-3">
                            <label className="text-xs font-semibold uppercase tracking-wide text-indigo-800">Teacher review</label>
                            <label className="mt-2 block text-sm font-medium text-indigo-950" htmlFor={`written-quality-${step.step_id}`}>Evidence quality</label>
                            <select
                              id={`written-quality-${step.step_id}`}
                              className="mt-1 w-full rounded border border-indigo-200 bg-white px-3 py-2 text-sm text-gray-800"
                              value={writtenQualityRatings[step.step_id] || ''}
                              onChange={(event) => setWrittenQualityRatings((current) => ({
                                ...current,
                                [step.step_id]: event.target.value as WrittenQualityRating | '',
                              }))}
                            >
                              <option value="">Not yet rated</option>
                              {writtenQualityOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label} — {option.description}</option>
                              ))}
                            </select>
                            <textarea
                              className="mt-2 w-full rounded border border-indigo-200 bg-white px-3 py-2 text-sm text-gray-800"
                              rows={3}
                              value={reviewNotes[step.step_id] || ''}
                              onChange={(event) => setReviewNotes((current) => ({ ...current, [step.step_id]: event.target.value }))}
                              placeholder="Feedback on this written response"
                            />
                            <div className="mt-2 flex flex-wrap gap-2">
                              <button type="button" disabled={saving || !writtenQualityRatings[step.step_id]} onClick={() => { void saveResponseReview(step, 'reviewed'); }} className="rounded bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
                                {step.latest_response.ai_analysis?.result.missing_or_unclear?.length ? 'Mark follow-up complete / reviewed' : 'Mark reviewed'}
                              </button>
                              <button type="button" disabled={saving || !writtenQualityRatings[step.step_id]} onClick={() => { void saveResponseReview(step, 'needs_revision'); }} className="rounded border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-800 disabled:opacity-50">Needs revision</button>
                            </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>}
            {detail.purpose === 'assessment' && (
              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Observed and verbal evidence</h2>
                    <p className="text-sm text-gray-600">Record evidence that cannot be captured by student-entered answers.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" disabled={saving} onClick={() => { void addObservation('hands_on'); }} className="rounded border border-gray-300 px-3 py-1.5 text-sm font-semibold">Add hands-on verification</button>
                    <button type="button" disabled={saving} onClick={() => { void addObservation('verbal'); }} className="rounded border border-gray-300 px-3 py-1.5 text-sm font-semibold">Add verbal verification</button>
                  </div>
                </div>
                {detail.steps.some((step) => step.content?.observation_config) && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Assessment verification tasks</p>
                    {detail.steps.filter((step) => step.content?.observation_config).map((step) => {
                      const config = step.content.observation_config || {};
                      const kind = config.kind === 'verbal' ? 'verbal' : 'hands_on';
                      const verified = (detail.observations || []).some((observation) => (
                        observation.lesson_step_id === step.step_id && observation.outcome === 'verified'
                      ));
                      return (
                        <div key={step.step_id} className="flex flex-wrap items-center justify-between gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                          <div>
                            <span className="font-semibold">{config.skill_label || step.title}</span>
                            <p className="text-xs text-gray-600">{kind === 'verbal' ? 'Verbal explanation or demonstration' : 'Hands-on observation'}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {verified && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">Verified</span>}
                            <button type="button" disabled={saving} onClick={() => { void addObservation(kind, step); }} className="rounded bg-indigo-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                              {verified ? 'Record updated verification' : 'Record verification'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {(detail.observations || []).length > 0 && (
                  <div className="mt-3 space-y-2">
                    {(detail.observations || []).map((row) => (
                      <div key={row.id} className="rounded border border-gray-100 bg-gray-50 px-3 py-2 text-sm">
                        <span className="font-semibold">{humanizeValue(row.kind)}:</span> {row.skill_label} — {humanizeValue(row.outcome)}
                        {row.notes ? <p className="mt-1 text-gray-600">{row.notes}</p> : null}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {detail.purpose === 'assessment' && (
              <section className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-indigo-950">Evidence summary and teacher verification</h2>
                    <p className="mt-1 text-xs text-indigo-900">AI can draft these fields from privacy-minimized evidence. You must review, edit, and save them.</p>
                  </div>
                  {detail.assessment_ai_review?.enabled && (
                    <button
                      type="button"
                      disabled={aiDraftingVerification || !detail.assessment_workflow?.teacher_review_complete}
                      onClick={() => { void draftVerification(); }}
                      className="rounded bg-violet-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                    >
                      {aiDraftingVerification ? 'Generating draft…' : 'Generate AI suggestions'}
                    </button>
                  )}
                </div>
                {!detail.assessment_ai_review?.enabled && <p className="mt-2 text-xs font-semibold text-violet-800">Privacy-controlled AI review is currently disabled.</p>}
                {aiVerificationDraftError && <p className="mt-2 rounded border border-rose-200 bg-rose-50 px-2 py-1.5 text-xs text-rose-700">{aiVerificationDraftError}</p>}
                {aiVerificationDraftPrivacy && (
                  <p className="mt-2 rounded border border-violet-200 bg-violet-50 px-2 py-1.5 text-xs text-violet-900">
                    Advisory draft loaded but not saved. Identity sent: No{aiVerificationDraftPrivacy.redaction_count ? ` · ${aiVerificationDraftPrivacy.redaction_count} personal-data redaction(s)` : ''}.
                  </p>
                )}
                <label className="mt-3 block text-sm font-medium text-indigo-950">Concise evidence summary</label>
                <textarea className="mt-1 w-full rounded border border-indigo-200 bg-white px-3 py-2 text-sm" rows={3} value={evidenceSummary} onChange={(event) => setEvidenceSummary(event.target.value)} placeholder="Summarize the evidence without assigning an official Headrush grade." />
                <label className="mt-3 block text-sm font-medium text-indigo-950">Teacher feedback</label>
                <textarea className="mt-1 w-full rounded border border-indigo-200 bg-white px-3 py-2 text-sm" rows={3} value={teacherFeedback} onChange={(event) => setTeacherFeedback(event.target.value)} />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={saving || !detail.assessment_workflow?.teacher_review_complete}
                    onClick={() => { void saveVerification(); }}
                    className="rounded bg-indigo-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {detail.verification ? 'Update teacher verification' : 'Save teacher verification'}
                  </button>
                  {detail.student_status === 'completed' && <button type="button" disabled={saving} onClick={() => { void downloadEvidence(); }} className="rounded border border-indigo-300 bg-white px-4 py-2 text-sm font-semibold text-indigo-800 disabled:opacity-50">Download evidence PDF</button>}
                </div>
                {detail.assessment_workflow?.pdf_available && !detail.assessment_workflow.teacher_review_complete && (
                  <p className="mt-2 text-xs text-amber-800">The PDF can be downloaded now, but it will show the currently incomplete review evidence.</p>
                )}
                <p className="mt-3 text-xs text-indigo-800">Headrush remains the official Level Up and standards grading record.</p>
              </section>
            )}
          </>
        )}
      </div>
      <nav
        aria-label="Assessment review page controls"
        className="fixed bottom-4 right-4 z-40 flex items-center gap-1 rounded-lg border border-gray-300 bg-white/95 p-1.5 shadow-lg backdrop-blur"
      >
        <button
          type="button"
          className="rounded px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-60"
          onClick={() => { void refreshReview(); }}
          disabled={loading || refreshing || saving || Boolean(aiBatchProgress)}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
        <button
          type="button"
          className="rounded px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Go to top of assessment review"
          title="Go to top"
        >
          ↑ Top
        </button>
        <button
          type="button"
          className="rounded px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
          onClick={() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })}
          aria-label="Go to bottom of assessment review"
          title="Go to bottom"
        >
          ↓ Bottom
        </button>
      </nav>
    </OptionalAppShell>
  );
};

export default V2TeacherAssignmentReview;
